import { useSendWelcomeEmails } from '@/hooks/queries/users';
import {
  formatActivationStatus,
  getActivationStatus,
  hasBeenInvited,
  User,
} from '@/types/user';
import { Icon } from '@iconify/react';
import {
  Button,
  Checkbox,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
} from '@nextui-org/react';
import { useMemo, useState } from 'react';

interface SendInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: User[];
  onSuccess?: () => void;
}

function pluralizeUsers(count: number): string {
  return `${count} user${count === 1 ? '' : 's'}`;
}

function getDisplayName(user: User): string {
  const name = [user.first_name, user.last_name]
    .filter((part) => part && part.trim() !== '')
    .join(' ');

  return name || user.email;
}

function InvitationStatusChip({ user }: { user: User }) {
  const status = getActivationStatus(user);
  const color =
    status === 'activated'
      ? 'success'
      : status === 'invited'
        ? 'primary'
        : status === 'not_invited'
          ? 'warning'
          : 'default';

  return (
    <Chip size="sm" variant="flat" color={color}>
      {formatActivationStatus(status)}
    </Chip>
  );
}

function InvitationNote() {
  return (
    <div className="flex items-start gap-2 rounded-medium bg-default-50 p-3">
      <Icon
        icon="lucide:info"
        className="mt-0.5 h-4 w-4 shrink-0 text-default-500"
      />
      <span className="text-sm text-default-600">
        Choose how recipients will finish setting up. SSO sends a welcome link
        into their organization sign-in. Set up password sends a link to create
        a password and sign in.
      </span>
    </div>
  );
}

export default function SendInvitationModal({
  isOpen,
  onClose,
  selectedUsers,
  onSuccess,
}: SendInvitationModalProps) {
  const [includeAlreadyInvited, setIncludeAlreadyInvited] = useState(false);
  const [isSSO, setIsSSO] = useState<boolean | null>(null);

  const alreadyInvited = useMemo(
    () => selectedUsers.filter(hasBeenInvited),
    [selectedUsers]
  );

  const recipients = useMemo(
    () =>
      includeAlreadyInvited
        ? selectedUsers
        : selectedUsers.filter((user) => !hasBeenInvited(user)),
    [selectedUsers, includeAlreadyInvited]
  );

  const { mutate: sendWelcomeEmails, isPending } = useSendWelcomeEmails({
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setIncludeAlreadyInvited(false);
    setIsSSO(null);
    onClose();
  };

  const handleSend = () => {
    if (recipients.length === 0 || isSSO === null) {
      return;
    }

    sendWelcomeEmails({
      user_ids: recipients.map((user) => user.id),
      isSSO,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      placement="center"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="text-medium">Send invitation email</ModalHeader>
        <ModalBody className="gap-4">
          {recipients.length > 0 && <InvitationNote />}

          {alreadyInvited.length > 0 && (
            <Checkbox
              size="sm"
              isSelected={includeAlreadyInvited}
              onValueChange={setIncludeAlreadyInvited}
            >
              <span className="text-sm text-default-600">
                Resend to {pluralizeUsers(alreadyInvited.length)} already
                invited
              </span>
            </Checkbox>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-tiny uppercase tracking-wide text-default-400">
              Recipients ({pluralizeUsers(selectedUsers.length)} selected)
            </span>
            {recipients.length === 0 ? (
              <p className="rounded-medium border border-dashed border-default-200 p-4 text-center text-sm text-default-500">
                Everyone selected has already been invited. Tick
                &quot;Resend&quot; above to email them again.
              </p>
            ) : (
              <div className="max-h-56 divide-y divide-default-100 overflow-y-auto rounded-medium border border-default-200">
                {recipients.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-default-700">
                        {getDisplayName(user)}
                      </p>
                      <p className="truncate text-tiny text-default-400">
                        {user.email}
                      </p>
                    </div>
                    <InvitationStatusChip user={user} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <RadioGroup
            label="Invitation type"
            orientation="horizontal"
            value={isSSO === null ? '' : isSSO ? 'true' : 'false'}
            onValueChange={(value) => setIsSSO(value === 'true')}
            classNames={{
              label: 'text-tiny uppercase tracking-wide text-default-400',
              wrapper: 'flex-row gap-6',
            }}
          >
            <Radio value="true">SSO welcome email</Radio>
            <Radio value="false">Set up password</Radio>
          </RadioGroup>
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSend}
            isLoading={isPending}
            isDisabled={recipients.length === 0 || isSSO === null}
          >
            Send to {pluralizeUsers(recipients.length)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
